import { useLayoutEffect, useRef, useState } from "react";
import { Layers, Lock, Trash2, Unlock } from "lucide-react";
import { mediaUrl } from "@/lib/api";

// Print-area geometry per mockup view. garmentIn = real width of the visible garment,
// garmentFrac = how much of the mockup image the garment spans, topFrac = where the
// printable area starts. Print size itself comes from the product's print_areas (inches),
// so artwork is always shown at true scale. Products can override via `view_geometry`.
export const VIEW_GEOMETRY = {
    front: { garmentIn: 20, garmentFrac: 0.58, topFrac: 0.233 },
    back: { garmentIn: 20, garmentFrac: 0.58, topFrac: 0.233 },
    left_sleeve: { garmentIn: 8, garmentFrac: 0.86, topFrac: 0.3 },
    right_sleeve: { garmentIn: 8, garmentFrac: 0.86, topFrac: 0.3 },
};

export const VIEW_LABELS = { front: "Front", back: "Back", left_sleeve: "L Sleeve", right_sleeve: "R Sleeve" };

export function garmentColors(product) {
    if (product?.colors?.length) return product.colors;
    return [{ name: product?.color_name || "White", hex: product?.shirt_color_hex || "#ffffff" }];
}

export default function ApparelCanvas({
    product, view, art, onArtChange,
    texts = [], onTextChange, selectedText = null, onSelectText,
    color,
}) {
    const boxRef = useRef(null);
    const [box, setBox] = useState({ w: 0, h: 0 });
    const [nat, setNat] = useState(null);
    const [artSel, setArtSel] = useState(false);
    const drag = useRef(null);

    useLayoutEffect(() => {
        const el = boxRef.current;
        if (!el) return;
        const ro = new ResizeObserver(() => setBox({ w: el.clientWidth, h: el.clientHeight }));
        ro.observe(el);
        setBox({ w: el.clientWidth, h: el.clientHeight });
        return () => ro.disconnect();
    }, []);

    useLayoutEffect(() => {
        const move = (e) => {
            const d = drag.current;
            if (!d) return;
            const p = e.touches ? e.touches[0] : e;
            if (d.kind === "resize") {
                const nw = Math.max(1, d.ow + (p.clientX - d.sx) / (d.ppi || 1));
                onArtChange({ ...art, widthIn: Math.round(nw * 10) / 10 });
                return;
            }
            const x = d.ox + (p.clientX - d.sx);
            const y = d.oy + (p.clientY - d.sy);
            if (d.kind === "art") onArtChange({ ...art, x, y });
            else onTextChange(d.idx, { ...texts[d.idx], x, y });
        };
        const up = () => { drag.current = null; };
        window.addEventListener("mousemove", move);
        window.addEventListener("mouseup", up);
        window.addEventListener("touchmove", move, { passive: false });
        window.addEventListener("touchend", up);
        return () => {
            window.removeEventListener("mousemove", move);
            window.removeEventListener("mouseup", up);
            window.removeEventListener("touchmove", move);
            window.removeEventListener("touchend", up);
        };
    }, [art, onArtChange, texts, onTextChange]);

    const mock = (product.mockups || {})[view];
    const area = (product.print_areas || {})[view] || { max_width_in: 15.5, max_height_in: 20 };
    const geo = (product.view_geometry || {})[view] || VIEW_GEOMETRY[view] || VIEW_GEOMETRY.front;
    const tint = product.tintable && color?.hex && color.hex.toLowerCase() !== "#ffffff" ? color.hex : null;

    const scale = nat && box.w ? Math.min(box.w / nat.w, box.h / nat.h) : 0;
    const dispW = nat ? nat.w * scale : 0;
    const dispH = nat ? nat.h * scale : 0;
    const offX = (box.w - dispW) / 2;
    const offY = (box.h - dispH) / 2;
    const ppi = dispW ? (geo.garmentFrac * dispW) / geo.garmentIn : 0;
    const areaW = area.max_width_in * ppi;
    const areaH = area.max_height_in * ppi;
    const areaLeft = offX + (dispW - areaW) / 2;
    const areaTop = offY + geo.topFrac * dispH;

    const artWIn = art?.widthIn || 0;
    const ratio = art?.ratio || 1;
    const artW = artWIn * ppi;
    const artH = artW / ratio;
    const artLeft = areaLeft + (areaW - artW) / 2 + (art?.x || 0);
    const artTop = areaTop + (areaH - artH) / 2 + (art?.y || 0);
    const overflow = art?.url && (artWIn > area.max_width_in + 0.01 || artWIn / ratio > area.max_height_in + 0.01);
    const locked = !!art?.locked;
    const showTools = art?.url && artSel;

    const start = (kind, idx) => (e) => {
        const p = e.touches ? e.touches[0] : e;
        const src = kind === "art" ? art : texts[idx];
        if (!src) return;
        e.stopPropagation();
        if (kind === "text") onSelectText?.(idx);
        if (kind === "art") setArtSel(true);
        if (src.locked) return;   // locked art/text can be selected but not dragged
        if (e.cancelable) e.preventDefault();
        drag.current = { kind, idx, sx: p.clientX, sy: p.clientY, ox: src.x || 0, oy: src.y || 0 };
    };

    const startResize = (e) => {
        if (!art || locked) return;
        const p = e.touches ? e.touches[0] : e;
        e.stopPropagation();
        if (e.cancelable) e.preventDefault();
        drag.current = { kind: "resize", sx: p.clientX, ow: art.widthIn, ppi };
    };

    const tool = (e, fn) => { e.stopPropagation(); e.preventDefault(); fn(); };

    return (
        <div ref={boxRef} onMouseDown={() => { onSelectText?.(null); setArtSel(false); }}
            className="relative isolate flex h-full w-full items-center justify-center overflow-hidden"
            data-testid="apparel-canvas">
            <div className="pointer-events-none absolute inset-0 opacity-60"
                style={{ backgroundImage: "linear-gradient(rgba(15,23,42,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,0.06) 1px, transparent 1px)", backgroundSize: "28px 28px" }} />

            {mock?.url ? (
                <img src={mediaUrl(mock.url)} alt={`${product.name} ${view}`} draggable={false}
                    onLoad={(e) => setNat({ w: e.target.naturalWidth || 1400, h: e.target.naturalHeight || 1248 })}
                    className="pointer-events-none absolute h-full w-full object-contain"
                    data-testid={`mockup-${view}`} />
            ) : (
                <p className="text-sm text-zinc-500">No mockup for this view</p>
            )}

            {tint && mock?.url && (
                <div className="pointer-events-none absolute inset-0" data-testid="garment-tint"
                    style={{
                        backgroundColor: tint, mixBlendMode: "multiply",
                        maskImage: `url(${mediaUrl(mock.url)})`, WebkitMaskImage: `url(${mediaUrl(mock.url)})`,
                        maskSize: "contain", WebkitMaskSize: "contain",
                        maskPosition: "center", WebkitMaskPosition: "center",
                        maskRepeat: "no-repeat", WebkitMaskRepeat: "no-repeat",
                    }} />
            )}

            {nat && (
                <>
                    {art?.url && (
                        <img src={art.url} alt="artwork" draggable={false} onMouseDown={start("art")} onTouchStart={start("art")}
                            className={`absolute select-none touch-none ${art.onTop ? "z-40" : "z-20"} ${locked ? "cursor-default" : "cursor-move"} ${overflow ? "ring-2 ring-red-500" : artSel ? "ring-2 ring-brand-green" : ""}`}
                            style={{ left: artLeft, top: artTop, width: artW, height: artH }}
                            data-testid="apparel-artwork" />
                    )}

                    {/* selection toolbar + resize handle */}
                    {showTools && (
                        <>
                            <div className="absolute z-50 flex items-center gap-0.5 rounded-full border border-zinc-200 bg-white/95 p-0.5 shadow-md"
                                style={{ left: artLeft, top: Math.max(2, artTop - 34) }} data-testid="art-toolbar"
                                onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
                                <button type="button" data-testid="art-tool-lock" title={locked ? "Unlock" : "Lock"}
                                    onClick={(e) => tool(e, () => onArtChange({ ...art, locked: !locked }))}
                                    className={`grid h-7 w-7 place-items-center rounded-full transition-colors hover:bg-zinc-100 ${locked ? "text-red-600" : "text-zinc-700"}`}>
                                    {locked ? <Lock size={14} /> : <Unlock size={14} />}
                                </button>
                                <button type="button" data-testid="art-tool-layer" title={art.onTop ? "Send behind text" : "Bring above text"}
                                    onClick={(e) => tool(e, () => onArtChange({ ...art, onTop: !art.onTop }))}
                                    className={`grid h-7 w-7 place-items-center rounded-full transition-colors hover:bg-zinc-100 ${art.onTop ? "text-brand-green" : "text-zinc-700"}`}>
                                    <Layers size={14} />
                                </button>
                                <button type="button" data-testid="art-tool-delete" title="Delete"
                                    onClick={(e) => tool(e, () => { onArtChange(null); setArtSel(false); })}
                                    className="grid h-7 w-7 place-items-center rounded-full text-zinc-700 transition-colors hover:bg-red-50 hover:text-red-600">
                                    <Trash2 size={14} />
                                </button>
                            </div>
                            {!locked && (
                                <span onMouseDown={startResize} onTouchStart={startResize} data-testid="art-resize-handle"
                                    className="absolute z-50 grid h-5 w-5 cursor-nwse-resize touch-none place-items-center rounded-full border-2 border-brand-green bg-white shadow"
                                    style={{ left: artLeft + artW - 10, top: artTop + artH - 10 }}>
                                    <span className="h-1.5 w-1.5 rounded-full bg-brand-green" />
                                </span>
                            )}
                        </>
                    )}

                    {texts.map((t, i) => (
                        <div key={i} onMouseDown={start("text", i)} onTouchStart={start("text", i)}
                            className={`absolute z-30 cursor-move select-none touch-none whitespace-pre text-center leading-none ${selectedText === i ? "outline-dashed outline-1 outline-brand-green" : ""}`}
                            style={{
                                left: areaLeft + areaW / 2 + (t.x || 0), top: areaTop + areaH / 2 + (t.y || 0),
                                transform: "translate(-50%, -50%)",
                                fontFamily: t.font, color: t.color,
                                fontSize: Math.max(6, (t.heightIn || 2) * ppi),
                                letterSpacing: `${(t.tracking || 0) * 0.01}em`,
                                WebkitTextStroke: t.outline ? `${Math.max(1, ppi * 0.04)}px ${t.outline}` : undefined,
                            }}
                            data-testid={`apparel-text-${i}`}>
                            {t.text}
                        </div>
                    ))}

                    {art?.url && (
                        <div className="pointer-events-none absolute bottom-3 left-3 z-30 rounded-full border border-white/15 bg-black/70 px-3 py-1 font-mono text-[11px] uppercase tracking-widest text-zinc-300"
                            data-testid="art-size-readout">
                            Design scales with each shirt size
                            {overflow && <span className="ml-2 text-red-400">too big</span>}
                            {locked && <span className="ml-2 text-amber-300">locked</span>}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
